import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaCog, FaFileAlt, FaHeart, FaCommentAlt, FaSignOutAlt, FaCrown } from 'react-icons/fa';
import { Icon } from './Icon';
import { config } from '../../config';

const ProfileContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  border: 0px ;
  transition: border-color 0.2s ease;

  &:hover {
    transform: scale(.95);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 0.2s ease;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #333;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
    border-top: 1px solid #eee;
    color: #dc3545;

    &:hover {
      background-color: #ffeaea;
    }
  }
`;

interface ProfileDropdownProps {
  profileImage?: string;
  onSignOut: () => void;
  onSettings?: () => void;
  onMyScripts?: () => void;
  onFavorites?: () => void;
  onSendFeedback?: () => void;
  onSubscription?: () => void;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  profileImage,
  onSignOut,
  onSettings,
  onMyScripts,
  onFavorites,
  onSendFeedback,
  onSubscription
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const defaultImage = '/images/profile/default48.png';

  //console.log('profileImage', profileImage);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <ProfileContainer ref={dropdownRef}>
      <ProfileImage
        src={profileImage || defaultImage}
        alt="Profile"
        onClick={() => setIsOpen(!isOpen)}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = defaultImage;
        }}
      />
      
      <DropdownMenu $isOpen={isOpen}>
        {onSettings && (
          <MenuItem onClick={() => handleMenuItemClick(onSettings)}>
            <Icon icon={FaCog} size={16} />
            Settings
          </MenuItem>
        )}
        
        {onMyScripts && (
          <MenuItem onClick={() => handleMenuItemClick(onMyScripts)}>
            <Icon icon={FaFileAlt} size={16} />
            My Scripts
          </MenuItem>
        )}
        
        {onFavorites && (
          <MenuItem onClick={() => handleMenuItemClick(onFavorites)}>
            <Icon icon={FaHeart} size={16} />
            Favorites
          </MenuItem>
        )}

        {onSubscription && (
          <MenuItem onClick={() => handleMenuItemClick(onSubscription)}>
            <Icon icon={FaCrown} size={16} />
            Subscription
          </MenuItem>
        )}
        
        {onSendFeedback && (
          <MenuItem onClick={() => handleMenuItemClick(onSendFeedback)}>
            <Icon icon={FaCommentAlt} size={16} />
            Send Feedback
          </MenuItem>
        )}
        
        <MenuItem onClick={() => handleMenuItemClick(onSignOut)}>
          <Icon icon={FaSignOutAlt} size={16} />
          Sign Out
        </MenuItem>
      </DropdownMenu>
    </ProfileContainer>
  );
};

export default ProfileDropdown; 